import axiosInstance from 'api/api-service';
import { COMPANY, PAYMENT, RETAILERS } from 'constants/api-endpoints';
import { TIME_FRAMES } from 'constants/enums';
import { HttpResponse } from 'models/http-interface';
import {
  ListParams,
  PoliciesLedgerParams,
  PoliciesLedgerResponse,
} from 'models/retailer-interface';

/**
 * @desc: create retailer
 * @param body
 * @returns
 */
export const createRetailer = (body: FormData) => {
  return axiosInstance.post<HttpResponse<null>>(`${RETAILERS.RETAILER}`, body);
};

/**
 * @desc: delete retailer
 * @param retailer_id
 * @returns
 */
export const deleteRetailer = (retailer_id: string) => {
  return axiosInstance.delete<HttpResponse<null>>(
    `${RETAILERS.RETAILER}/${retailer_id}`
  );
};

/**
 * @desc: get all retailers
 * @param params
 * @returns
 */
export const getRetailersList = (params: ListParams) => {
  return axiosInstance.get<any>(`${RETAILERS.RETAILERS}`, {
    params,
  });
};

/**
 * @desc: update retailer by id
 * @param id
 * @param updatedBody
 * @returns
 */
export const updateRetailer = (id: string, updatedBody: FormData) => {
  return axiosInstance.put<HttpResponse<null>>(
    `${RETAILERS.RETAILER}/${id}`,
    updatedBody
  );
};

/**
 * @desc: to verify the reset password token
 * @param token
 * @returns
 */
export const checkCompanyId = () =>
  axiosInstance.get<HttpResponse<null>>(`${COMPANY.COMPANY}`);

/**
 * @desc: verify the retailer
 * @param params
 * @returns
 */
export const verifyRetailer = (retailerId: string) => {
  return axiosInstance.get<any>(`${RETAILERS.RETAILER}/${retailerId}`);
};

/**
 * @desc: to send reset password email
 * @param retailer_id
 * @returns
 */
export const sendResetPasswordEmail = (retailer_id: string) => {
  return axiosInstance.post<HttpResponse<null>>(
    `${RETAILERS.RETAILER_RESET_PASSWORD}`,
    { retailer_id }
  );
};

/**
 * @desc: to get stats
 * @param retailer_id
 * @returns
 */
export const getRetailerStatistics = (data: {
  retailerId: string;
  startDate: Date;
  endDate: Date;
}) => {
  return axiosInstance.post<HttpResponse<null>>(
    `${RETAILERS.RETAILERS_STATS}`,
    data
  );
};
export const getRetailerGraphs = (data: {
  retailerId: string;
  startDate: Date;
  endDate: Date;
  timeFrame: TIME_FRAMES;
}) => {
  return axiosInstance.post<HttpResponse<null>>(
    `${RETAILERS.RETAILERS_GRAPH}`,
    data
  );
};

/**
 * @desc: to get admin stats
 * @param retailer_id
 * @returns
 */
export const getAdminPoliciesListing = (data: {
  startDate: Date;
  endDate: Date;
}) => {
  return axiosInstance.get<
    HttpResponse<{
      tableData: {
        columnsData: Array<{ header: string; [key: string]: any }>;
        rowData: Array<{
          products_info: Array<Record<string, unknown>>;
          [key: string]: unknown;
        }>;
      };
    }>
  >(`${RETAILERS.ADMIN_STATS}`, {
    params: {
      startDate: data.startDate.toString(),
      endDate: data.endDate.toString(),
    },
  });
};

/**
 * @desc: to get the server-paginated policies ledger
 * @param params
 * @returns
 */
export const getPoliciesLedger = (params: PoliciesLedgerParams) => {
  return axiosInstance.get<HttpResponse<PoliciesLedgerResponse>>(
    `${RETAILERS.POLICIES_LEDGER}`,
    {
      params,
    }
  );
};

/**
 * @desc: to trigger a full Stripe refund for a paid policy (admin action).
 * The response means "initiated" — the final refunded state arrives on the
 * order via Stripe webhook.
 * @param companySlug
 * @param uuid order uuid
 * @returns
 */
export const triggerRefund = (companySlug: string, uuid: string) => {
  return axiosInstance.post<
    HttpResponse<{ uuid: string; refundId: string; status: string }>
  >(`${PAYMENT.REFUND}/${companySlug}`, { uuid });
};
